export type SearchHighlightSegment = {
  text: string;
  highlight: boolean;
};

/** Same classes as `SearchHighlightText.vue` for consistent styling. */
export const MARK_CLASS = 'search-highlight-text';

/**
 * Splits display text into segments for case-insensitive search highlighting
 * without building a RegExp from the query (safe for metacharacters like `(`, `*`, `[`).
 *
 * Render segments in Vue with `<mark v-if="s.highlight">` / text nodes — avoid `v-html`
 * so user-controlled query never becomes executable markup.
 *
 * @remarks Matching uses parallel lowercased strings (`toLocaleLowerCase()`). In rare
 * Unicode case-folding cases, folded length can differ from the original slice; callers
 * needing strict grapheme alignment should use a dedicated search/normalization path.
 */
export function splitTextForSearchHighlight(
  displayText: string,
  rawQuery: string
): SearchHighlightSegment[] {
  const query = rawQuery.trim();
  if (!query) {
    return [{ text: displayText, highlight: false }];
  }
  if (!displayText) {
    return [{ text: '', highlight: false }];
  }

  const lower = displayText.toLocaleLowerCase();
  const q = query.toLocaleLowerCase();
  const out: SearchHighlightSegment[] = [];
  let i = 0;

  while (i < displayText.length) {
    const j = lower.indexOf(q, i);
    if (j === -1) {
      out.push({ text: displayText.slice(i), highlight: false });
      break;
    }
    if (j > i) {
      out.push({ text: displayText.slice(i, j), highlight: false });
    }
    const end = j + q.length;
    out.push({ text: displayText.slice(j, end), highlight: true });
    i = end;
  }

  return out;
}
